/**
 * EmailSetupWizard - 4-step guided email setup flow (AC-1108)
 *
 * Steps:
 * 1. Provider Selection (Gmail, Yahoo, Outlook, Other)
 * 2. Credentials Form (email + app password)
 * 3. Test Connection
 * 4. Done / Success
 *
 * Technical Design Reference: AC-1108
 * Epic: AC-1081
 */
import React, { useState, useEffect } from 'react';
import {
  Mail,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Loader,
  X,
  Eye,
  EyeOff,
  HelpCircle,
  ExternalLink,
} from 'lucide-react';
import apiService from '../../services/api';
import { EmailProviderPreset, SaveCredentialRequest } from '../../types';

export interface EmailSetupWizardProps {
  builtinServerId: number;
  credentialId?: number | null;
  onClose: () => void;
  onSaveComplete: () => void;
}

type WizardStep = 'provider' | 'credentials' | 'testing' | 'done';

const EmailSetupWizard: React.FC<EmailSetupWizardProps> = ({
  builtinServerId,
  credentialId = null,
  onClose,
  onSaveComplete,
}) => {
  const isEditMode = credentialId !== null && credentialId !== undefined;
  const [step, setStep] = useState<WizardStep>(isEditMode ? 'credentials' : 'provider');
  const [providers, setProviders] = useState<EmailProviderPreset[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<EmailProviderPreset | null>(null);
  const [email, setEmail] = useState('');
  const [appPassword, setAppPassword] = useState('');
  const [accountName, setAccountName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testError, setTestError] = useState<string | null>(null);
  const [isLoadingProviders, setIsLoadingProviders] = useState(true);

  // Fetch providers on mount, and load existing credential in edit mode
  useEffect(() => {
    const init = async () => {
      try {
        setIsLoadingProviders(true);
        const data = await apiService.getEmailProviders();
        setProviders(data);

        // In edit mode, load existing credential data to prepopulate fields
        if (isEditMode && credentialId) {
          const credentials = await apiService.listBuiltinCredentials(builtinServerId);
          const existing = credentials.find(c => c.credential_id === credentialId);
          if (existing) {
            if (existing.email) setEmail(existing.email);
            if (existing.instance_label) setAccountName(existing.instance_label);
            // Match the provider so help text and test connection work
            const matchedProvider = data.find(p => p.id === existing.provider);
            if (matchedProvider) {
              setSelectedProvider(matchedProvider);
            } else if (existing.provider) {
              // Create a synthetic provider for custom/unknown providers so test button works
              setSelectedProvider({
                id: existing.provider,
                name: existing.instance_label || existing.provider,
                imap_host: '', imap_port: 993,
                smtp_host: '', smtp_port: 587,
                help_url: '', help_steps: [], why: '',
              });
            }
          }
        }
      } catch (err) {
        // Silently handle - providers will be empty
      } finally {
        setIsLoadingProviders(false);
      }
    };
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectProvider = (provider: EmailProviderPreset) => {
    setSelectedProvider(provider);
    setStep('credentials');
  };

  const handleCredentialsNext = () => {
    setStep('testing');
  };

  const handleTestConnection = async () => {
    if (!selectedProvider) return;

    setIsTesting(true);
    setTestError(null);

    try {
      const requestData: SaveCredentialRequest = {
        provider: selectedProvider.id,
        instance_label: accountName || undefined,
        email,
        app_password: appPassword,
        imap_host: selectedProvider.imap_host,
        imap_port: selectedProvider.imap_port,
        smtp_host: selectedProvider.smtp_host,
        smtp_port: selectedProvider.smtp_port,
      };

      const result = await apiService.testBuiltinConnection(builtinServerId, credentialId, requestData);

      if (result.success) {
        // Auto-save credentials on successful test
        if (isEditMode && credentialId) {
          await apiService.updateBuiltinCredentials(builtinServerId, credentialId, requestData);
        } else {
          await apiService.saveBuiltinCredentials(builtinServerId, requestData);
        }
        setStep('done');
      } else {
        setTestError(result.message || 'Connection test failed');
      }
    } catch (err: any) {
      setTestError(err.message || 'Connection test failed');
    } finally {
      setIsTesting(false);
    }
  };

  const handleDone = () => {
    onSaveComplete();
  };

  const handleBack = () => {
    if (step === 'credentials' && isEditMode) {
      onClose();
    } else if (step === 'credentials') {
      setStep('provider');
    } else if (step === 'testing') {
      setStep('credentials');
    }
  };

  // ---- Render Steps ----

  const renderProviderStep = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-gray-900 dark:text-warm-white">
        Choose your email provider
      </h3>
      {isLoadingProviders ? (
        <div className="flex items-center justify-center py-8">
          <Loader className="w-6 h-6 animate-spin text-primary-600" />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {providers.map((provider) => (
            <button
              key={provider.id}
              onClick={() => handleSelectProvider(provider)}
              className="flex items-center gap-3 p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors text-left"
            >
              <Mail className="w-5 h-5 text-gray-500" />
              <span className="text-sm font-medium text-gray-900 dark:text-warm-white">
                {provider.name}
              </span>
              <ChevronRight className="w-4 h-4 ml-auto text-gray-400" />
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const renderCredentialsStep = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-gray-900 dark:text-warm-white">
        {isEditMode ? 'Update email credentials' : `${selectedProvider?.name || 'Email'} - Enter your credentials`}
      </h3>

      <div>
        <label
          htmlFor="email-address"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Email Address
        </label>
        <input
          id="email-address"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-warm-white placeholder-gray-400 dark:placeholder-gray-500"
        />
      </div>

      <div>
        <label
          htmlFor="app-password"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          App Password
        </label>
        <div className="relative">
          <input
            id="app-password"
            type={showPassword ? 'text' : 'password'}
            value={appPassword}
            onChange={(e) => setAppPassword(e.target.value)}
            placeholder={isEditMode ? 'Re-enter your app password' : 'xxxx xxxx xxxx xxxx'}
            className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-warm-white placeholder-gray-400 dark:placeholder-gray-500"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
          >
            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        {isEditMode && (
          <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
            For security, your app password is not stored in the browser. Please re-enter it to save changes.
          </p>
        )}
      </div>

      <div>
        <label
          htmlFor="account-name"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Account Name <span className="text-xs text-gray-400 dark:text-gray-500">(optional)</span>
        </label>
        <input
          id="account-name"
          type="text"
          value={accountName}
          onChange={(e) => setAccountName(e.target.value)}
          placeholder={selectedProvider?.name || 'e.g., Work Gmail, Personal Yahoo'}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-warm-white placeholder-gray-400 dark:placeholder-gray-500"
        />
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          A friendly name to identify this email account when assigning tools to agents.
        </p>
      </div>

      {/* Help section */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-md">
        <button
          type="button"
          onClick={() => setShowHelp(!showHelp)}
          className="flex items-center justify-between w-full px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
        >
          <span className="flex items-center gap-2">
            <HelpCircle className="w-4 h-4" />
            How do I get an app password?
          </span>
          {showHelp ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        {showHelp && (
          <div className="px-4 pb-3 border-t border-gray-200 dark:border-gray-700">
            {selectedProvider && selectedProvider.help_steps.length > 0 ? (
              <>
                <ol className="list-decimal list-inside space-y-1 mt-2 text-sm text-gray-600 dark:text-gray-400">
                  {selectedProvider.help_steps.map((helpStep, index) => (
                    <li key={index}>{helpStep}</li>
                  ))}
                </ol>
                {selectedProvider.help_url && (
                  <a
                    href={selectedProvider.help_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 mt-2 text-sm text-primary-600 hover:text-primary-700"
                  >
                    Learn more <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </>
            ) : (
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                An app password is a special password that lets third-party apps access your email.
                Check your email provider's security settings to generate one.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-between pt-2">
        <button
          type="button"
          onClick={handleBack}
          className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          Back
        </button>
        <button
          type="button"
          onClick={handleCredentialsNext}
          disabled={!email || !appPassword}
          className="px-4 py-2 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
    </div>
  );

  const renderTestStep = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-gray-900 dark:text-warm-white">
        Test Connection
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400">
        We will verify your email credentials by connecting to {selectedProvider?.name}.
      </p>

      {testError && (
        <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
          <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-red-800 dark:text-red-300">
            <p className="font-medium">Connection failed</p>
            <p>{testError}</p>
          </div>
        </div>
      )}

      <div className="flex justify-between pt-2">
        <button
          type="button"
          onClick={handleBack}
          className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          Back
        </button>
        <button
          type="button"
          onClick={handleTestConnection}
          disabled={isTesting}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isTesting ? (
            <>
              <Loader className="w-4 h-4 animate-spin" />
              Testing...
            </>
          ) : (
            'Test Connection'
          )}
        </button>
      </div>
    </div>
  );

  const renderDoneStep = () => (
    <div className="space-y-4 text-center">
      <CheckCircle className="w-12 h-12 text-green-500 mx-auto" />
      <h3 className="text-lg font-medium text-gray-900 dark:text-warm-white">
        Email Connected Successfully
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Your {selectedProvider?.name} account ({email}) has been connected.
        You can now use email tools in your workflows.
      </p>
      <div className="pt-2">
        <button
          type="button"
          onClick={handleDone}
          className="px-6 py-2 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700"
        >
          Done
        </button>
      </div>
    </div>
  );

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white dark:bg-cap-dark rounded-lg shadow-xl max-w-lg w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-warm-white flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Email Setup
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded"
            aria-label="Close"
            data-testid="wizard-close-button"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-5">
          {step === 'provider' && renderProviderStep()}
          {step === 'credentials' && renderCredentialsStep()}
          {step === 'testing' && renderTestStep()}
          {step === 'done' && renderDoneStep()}
        </div>
      </div>
    </div>
  );
};

export default EmailSetupWizard;
